import { AfterViewInit, Component, ContentChildren, forwardRef, Input, OnDestroy, QueryList, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TemplateDirective } from '@proget-shared';
import { Listbox, ListboxSort } from '@proget-shared/form/listbox';
import { ObjectHelper } from '@proget-shared/helper';
import { BehaviorSubject, combineLatest, map, noop, Subject, Subscription } from 'rxjs';

@Component({
  selector: 'app-pick-list',
  templateUrl: './pick-list.component.html',
  styleUrls: ['./pick-list.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => PickListComponent),
    multi: true,
  }],
  standalone: false,
})
export class PickListComponent<T = any> implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @Input()
  public valueKey = '';
  @Input()
  public sourceFilterPlaceholder = '';
  @Input()
  public targetFilterPlaceholder = '';
  @Input()
  public sort: ListboxSort = true;

  protected readonly form: FormGroup;

  protected availableItems: T[] = [];
  protected selectedItems: T[] = [];
  protected formDisabled = false;

  protected readonly subscription = new Subscription();
  protected readonly valueSubject = new Subject<any[]>();
  protected readonly changeSubject = new Subject<void>();
  protected readonly optionsSubject = new BehaviorSubject<T[]>([]);

  @ContentChildren(TemplateDirective)
  protected templates: QueryList<TemplateDirective>;
  @ViewChild('optionsListbox', { static: true })
  protected optionsListbox: Listbox;
  @ViewChild('selectedListbox', { static: true })
  protected selectedListbox: Listbox;

  private modelTouched: () => void = noop;
  private modelChanged: (value: any[]) => void = noop;
  private _options: T[] = [];

  constructor(formBuilder: FormBuilder) {
    this.form = formBuilder.group({
      availableItemsSelection: [],
      selectedItemsSelection: [],
    });

    this.subscription.add(
      combineLatest([this.valueSubject, this.optionsSubject])
        .pipe(
          map(
            ([value, options]) => value
              .map((valueItem) => options.find((optionItem) => ObjectHelper.equals(
                valueItem,
                this.getItemValue(optionItem)
              )))
              .filter((item) => void 0 !== item)
          )
        )
        .subscribe({
          next: (selectedItems) => {
            this.selectedItems = selectedItems instanceof Array ? selectedItems : [];
            this.availableItems = this.options.filter((option: T) => !this.isItemSelected(option));
            this.changeSubject.next();
          },
        })
    );
  }

  writeValue(value: any): void {
    this.valueSubject.next(value instanceof Array ? value : []);
  }

  registerOnChange(fn: (value: any[]) => void): void {
    this.modelChanged = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.modelTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.formDisabled = isDisabled;

    if (isDisabled) {
      this.form.disable();

      return;
    }

    this.form.enable();
  }

  ngAfterViewInit(): void {
    const itemTemplate = this.templates.find((template) => template.appTemplate === 'item')?.templateRef ?? null;

    this.optionsListbox.setItemTemplate(itemTemplate);
    this.selectedListbox.setItemTemplate(itemTemplate);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.valueSubject.complete();
    this.changeSubject.complete();
    this.optionsSubject.complete();
  }

  @Input()
  public set options(options: T[]) {
    this._options = options.slice();
    this.optionsSubject.next(options);
    this.valueSubject.next(this.selectedItems.map((item) => this.getItemValue(item)));
  }

  public get options(): T[] {
    return this._options;
  }

  protected clearValue(fieldName: string): void {
    this.form.get(fieldName).setValue(null);
  }

  protected canMoveSelection(source: string): boolean {
    const sourceControl = this.form.get(source) as FormControl;

    return !sourceControl.disabled && sourceControl.value;
  }

  protected addAllAvailableToSelected(): void {
    this.addItemsToSelected(this.availableItems);
    this.optionsListbox.clearFilter();
  }

  protected addSelectionToSelected(): void {
    this.addItemsToSelected(this.form.value.availableItemsSelection);
  }

  protected removeSelectionFromSelected(): void {
    this.removeItemsFromSelected(this.form.value.selectedItemsSelection);
  }

  protected removeAllFromSelected(): void {
    this.removeItemsFromSelected(this.selectedItems);
    this.selectedListbox.clearFilter();
  }

  protected addItemsToSelected(itemsToAdd: T[] | T): void {
    if (!itemsToAdd) {
      return;
    }

    const itemsArray: T[] = this.forceArray(itemsToAdd);

    this.emitValue([...itemsArray, ...this.selectedItems].map((item) => this.getItemValue(item)));

    this.form.get('availableItemsSelection').setValue(null);
    this.form.get('selectedItemsSelection').setValue(itemsArray);
  }

  protected removeItemsFromSelected(itemsToRemove: T[] | T): void {
    if (!itemsToRemove) {
      return;
    }

    const itemsArray: T[] = this.forceArray(itemsToRemove);

    this.emitValue(
      this.selectedItems
        .filter((selectedItem) => !itemsArray.some((item) => item === selectedItem))
        .map((item) => this.getItemValue(item))
    );

    this.form.get('availableItemsSelection').setValue(null);
    this.form.get('selectedItemsSelection').setValue(null);
  }

  protected isItemSelected(item: T): boolean {
    return this.selectedItems.some((selectedItem: T) => selectedItem === item);
  }

  private emitValue(value: any[]): void {
    this.valueSubject.next(value);
    this.modelTouched();
    this.modelChanged(value);
  }

  private getItemValue(item: any): any {
    if (!this.valueKey) {
      return item;
    }

    return item !== null && typeof item === 'object' ? item[this.valueKey] : null;
  }

  private forceArray(source: any): any[] {
    return Array.isArray(source) ? source.slice() : [source];
  }
}
